import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { issuesApi, type Issue, type IssueStatus } from "@/api/issues";
import { templatesApi } from "@/api/templates";
import { getTemplateMeta, TEMPLATE_FALLBACK_ICON } from "@/lib/templateMeta";
import { splitMdSections } from "@/components/MarkdownLite";
import { AUTO_ENTENDER_KEYS } from "@/lib/autoTasks";

/**
 * EntenderAutofeito — "fator UAU" do ESCALA. Assim que o usuário tem um
 * diagnóstico, o Mohdus já SAI PESQUISANDO sozinho o negócio dele: dispara
 * as tarefas de Entender que rodam sem integração externa (pesquisa de
 * tendências, benchmark e descoberta de concorrentes) e mostra cada uma
 * indo de "pesquisando…" → resultado destilado, ao vivo, embaixo do radar.
 *
 * Idempotente por design: cria só os cards que ainda não existem pra essa
 * company. Re-rodar o ESCALA não re-dispara pesquisa (custo controlado) —
 * a análise já existe e fica exibida; o usuário pode refazer manualmente
 * pelo card se quiser dado fresco.
 */

// Cards de Entender que rodam SEM integração que o usuário precise conectar
// (Perplexity/Apify usam chaves da ND). A lista vive em @/lib/autoTasks pra o
// "primeiros passos" também saber ignorá-las no check de "criar primeira tarefa".
// AUTO_ENTENDER_KEYS importado no topo.

const PENDING_STATUSES: IssueStatus[] = ["backlog", "todo", "in_progress"];
const READY_STATUSES: IssueStatus[] = ["in_review", "done"];

function isPending(status: IssueStatus) {
  return PENDING_STATUSES.includes(status);
}
function isReady(status: IssueStatus) {
  return READY_STATUSES.includes(status);
}

/** Pega o resumo destilado (primeira seção com texto) de um doc markdown. */
function distill(body: string): string {
  const sections = splitMdSections(body);
  const firstWithText =
    sections.find((s) => s.body.trim().length > 0) ?? sections[0];
  const raw = (firstWithText?.body ?? body)
    .replace(/[#*_>`-]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return raw.length > 220 ? `${raw.slice(0, 220).trim()}…` : raw;
}

export function EntenderAutofeito({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Keys que já pedimos pra criar nesta sessão — evita refire no polling/StrictMode.
  const requestedRef = useRef<Set<string>>(new Set());

  const issuesQuery = useQuery({
    queryKey: ["companies", companyId, "issues", "autofeito"],
    queryFn: () => issuesApi.list(companyId),
    enabled: !!companyId,
    refetchInterval: (query) => {
      const data = query.state.data as Issue[] | undefined;
      if (!data) return 3000;
      const anyPending = AUTO_ENTENDER_KEYS.some((key) => {
        const issue = latestByKey(data, key);
        return !issue || isPending(issue.status);
      });
      return anyPending ? 4000 : false;
    },
  });

  const issues = issuesQuery.data;

  // Garante que os 3 cards existem: cria os que faltam, uma única vez.
  useEffect(() => {
    if (!issues || !companyId) return;
    for (const templateKey of AUTO_ENTENDER_KEYS) {
      const existing = latestByKey(issues, templateKey);
      if (existing) continue;
      if (requestedRef.current.has(templateKey)) continue;
      requestedRef.current.add(templateKey);
      templatesApi
        .createIssue(companyId, { templateKey })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ["companies", companyId, "issues"],
          });
        })
        .catch(() => {
          // libera pra tentar de novo num próximo carregamento
          requestedRef.current.delete(templateKey);
        });
    }
  }, [issues, companyId, queryClient]);

  const cards = useMemo(
    () =>
      AUTO_ENTENDER_KEYS.map((key) => ({
        key,
        meta: getTemplateMeta(key),
        issue: issues ? latestByKey(issues, key) : null,
      })),
    [issues],
  );

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--nd-orange)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            display: "flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          <Sparkles size={12} color="var(--nd-orange)" />O que já descobri sobre
          seu negócio
        </div>
        <div
          style={{
            fontSize: 14,
            color: "var(--wf-ink-3)",
            fontFamily: "var(--wf-font-body)",
          }}
        >
          Enquanto você lê o diagnóstico, seus agentes já saíram pesquisando o
          mercado. As análises aparecem aqui assim que ficam prontas.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 16,
        }}
      >
        {cards.map(({ key, meta, issue }) => {
          const Icon = meta?.icon ?? TEMPLATE_FALLBACK_ICON;
          return (
            <AutofeitoCard
              key={key}
              label={meta?.label ?? key}
              tagline={meta?.tagline ?? ""}
              Icon={Icon}
              issue={issue}
              onOpen={(id, status) =>
                navigate(
                  isReady(status) ? `/resultado/${id}` : `/execucao/${id}`,
                )
              }
            />
          );
        })}
      </div>
    </section>
  );
}

/** Acha o issue mais recente de um templateKey (ignora cancelados). */
function latestByKey(issues: Issue[], templateKey: string): Issue | null {
  return (
    issues
      .filter((i) => i.templateKey === templateKey && i.status !== "cancelled")
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null
  );
}

function AutofeitoCard({
  label,
  tagline,
  Icon,
  issue,
  onOpen,
}: {
  label: string;
  tagline: string;
  Icon: React.ComponentType<{ size?: number; color?: string }>;
  issue: Issue | null;
  onOpen: (id: string, status: IssueStatus) => void;
}) {
  const status = issue?.status;
  const ready = !!status && isReady(status);
  const problem = status === "blocked";
  const pending = !issue || (!!status && isPending(status));

  // Resultado destilado: busca o doc só quando pronto.
  const docQuery = useQuery({
    queryKey: ["issues", issue?.id, "documents", "autofeito"],
    queryFn: () => issuesApi.documents(issue!.id),
    enabled: ready && !!issue,
    staleTime: 60_000,
  });
  const snippet = useMemo(() => {
    const doc = docQuery.data?.[0];
    const body = doc?.latestBody ?? doc?.body ?? "";
    return body ? distill(body) : "";
  }, [docQuery.data]);

  const clickable = !!issue;

  return (
    <div
      onClick={() => issue && onOpen(issue.id, issue.status)}
      style={{
        position: "relative",
        borderRadius: 16,
        padding: 20,
        minHeight: 168,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        border: "1px solid var(--wf-ink-5)",
        background: "var(--wf-paper)",
        cursor: clickable ? "pointer" : "default",
        transition: "border-color 160ms ease, box-shadow 160ms ease",
      }}
      onMouseEnter={(e) => {
        if (clickable) {
          e.currentTarget.style.borderColor = "var(--nd-orange)";
          e.currentTarget.style.boxShadow = "0 6px 20px -8px rgba(255,112,0,0.25)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--wf-ink-5)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            display: "grid",
            placeItems: "center",
            background: "rgba(255,112,0,0.12)",
            border: "1px solid rgba(255,112,0,0.25)",
            flexShrink: 0,
          }}
        >
          <Icon size={17} color="var(--nd-orange)" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <span
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "var(--wf-ink)",
              fontFamily: "var(--wf-font-display)",
            }}
          >
            {label}
          </span>
          <span
            style={{
              fontSize: 11.5,
              color: "var(--wf-ink-3)",
              fontFamily: "var(--wf-font-body)",
            }}
          >
            {tagline}
          </span>
        </div>
      </div>

      {pending && <PendingBody />}

      {problem && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            fontSize: 13,
            color: "var(--nd-orange-pun)",
            fontFamily: "var(--wf-font-body)",
          }}
        >
          <AlertTriangle size={14} color="var(--nd-orange-pun)" />
          Precisa da sua atenção, abra pra ver.
        </div>
      )}

      {ready && (
        <>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.5,
              color: "var(--wf-ink-2)",
              fontFamily: "var(--wf-font-body)",
              flex: 1,
            }}
          >
            {snippet || "Análise pronta."}
          </p>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--nd-orange)",
              fontFamily: "var(--wf-font-body)",
            }}
          >
            Ver análise completa <ArrowRight size={13} color="var(--nd-orange)" />
          </span>
        </>
      )}
    </div>
  );
}

function PendingBody() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: 13,
          fontWeight: 600,
          color: "var(--nd-orange)",
          fontFamily: "var(--wf-font-body)",
        }}
      >
        <Loader2 size={14} color="var(--nd-orange)" className="wf-spin" />
        Pesquisando na internet…
      </div>
      {/* skeleton bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {[92, 78, 60].map((w) => (
          <div
            key={w}
            className="wf-shimmer"
            style={{
              height: 9,
              width: `${w}%`,
              borderRadius: 5,
              background: "rgba(0,0,0,0.07)",
            }}
          />
        ))}
      </div>
    </div>
  );
}
